import { createAuthenticatedFetch } from "@assetwatch/auth";
import type { AppType } from "@assetwatch/modality-api/app";
import {
  asMachineId,
  assignmentOptionsSchema,
  createUploadResponseSchema,
  motionAmplificationJobDetailSchema,
  motionAmplificationJobDownloadUrlsSchema,
  motionAmplificationJobIdSchema,
  motionAmplificationJobListSchema,
  motionAmplificationRawVideoListSchema,
  reportJobFailureResponseSchema,
  startJobResponseSchema,
} from "@assetwatch/modality-api/motion-amplification/schemas";
import { hc } from "hono/client";
import type { z } from "zod";

const configuredModalityApiBaseUrl = (
  import.meta.env.VITE_MODALITY_API_BASE_URL as string | undefined
)?.replace(/\/$/, "");
const configuredModalityApiPathPrefix = (
  import.meta.env.VITE_MODALITY_API_PATH_PREFIX as string | undefined
)?.trim();
const modalityApiBaseUrl =
  configuredModalityApiBaseUrl ?? window.location.origin;
const modalityApiPathPrefix =
  configuredModalityApiPathPrefix === null ||
  configuredModalityApiPathPrefix === undefined
    ? "/modality"
    : configuredModalityApiPathPrefix === ""
      ? ""
      : configuredModalityApiPathPrefix.startsWith("/")
        ? configuredModalityApiPathPrefix.replace(/\/$/, "")
        : `/${configuredModalityApiPathPrefix.replace(/\/$/, "")}`;

export function createClientId(): string {
  const cryptoApi = globalThis.crypto;
  if (typeof cryptoApi?.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }
  if (typeof cryptoApi?.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join(
      "",
    );
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

const authenticatedFetch = createAuthenticatedFetch({
  createRequestId: createClientId,
});

const modalityClient = hc<AppType>(
  `${modalityApiBaseUrl}${modalityApiPathPrefix}`,
  {
    fetch: authenticatedFetch,
  },
);

export type MotionAmplificationJobId = z.infer<
  typeof motionAmplificationJobIdSchema
>;

export function asMotionAmplificationJobId(
  value: string,
): MotionAmplificationJobId {
  return motionAmplificationJobIdSchema.parse(value);
}

type MotionAmplificationAssignment = {
  customerId: string;
  customerName: string;
  facilityId: string;
  facilityName: string;
  lineName: string | null;
  machineId: string;
  machineName: string;
};
type MotionAmplificationJobSummary = Omit<
  z.infer<typeof motionAmplificationJobListSchema>["jobs"][number],
  "jobId" | "assignment"
> & {
  jobId: MotionAmplificationJobId;
  assignment: MotionAmplificationAssignment | null;
};
type MotionAmplificationJobDetail = Omit<
  z.infer<typeof motionAmplificationJobDetailSchema>,
  "jobId" | "assignment"
> & {
  jobId: MotionAmplificationJobId;
  assignment: MotionAmplificationAssignment | null;
};

export type MotionAmplificationUploadResponse = Omit<
  z.infer<typeof createUploadResponseSchema>,
  "jobId"
> & { jobId: MotionAmplificationJobId };
export type MotionAmplificationRawVideo = z.infer<
  typeof motionAmplificationRawVideoListSchema
>["rawVideos"][number];
export type MotionAmplificationJob = MotionAmplificationJobSummary;
export type MotionAmplificationJobsResponse = Omit<
  z.infer<typeof motionAmplificationJobListSchema>,
  "jobs"
> & { jobs: MotionAmplificationJob[] };
export type MotionAmplificationRawVideosResponse = z.infer<
  typeof motionAmplificationRawVideoListSchema
>;
export type MotionAmplificationJobDownloadUrls = z.infer<
  typeof motionAmplificationJobDownloadUrlsSchema
>;
export type MotionAmplificationAssignmentOption = z.infer<
  typeof assignmentOptionsSchema
>["options"][number];

function withMotionAmplificationJobId<T extends { jobId: string }>(value: T) {
  return { ...value, jobId: asMotionAmplificationJobId(value.jobId) };
}

function withMotionAmplificationJobAssignment<
  T extends { jobId: string; assignment?: unknown },
>(
  value: T,
): Omit<T, "jobId" | "assignment"> & {
  jobId: MotionAmplificationJobId;
  assignment: Exclude<T["assignment"], undefined> | null;
} {
  return {
    ...value,
    jobId: asMotionAmplificationJobId(value.jobId),
    assignment: value.assignment ?? null,
  } as Omit<T, "jobId" | "assignment"> & {
    jobId: MotionAmplificationJobId;
    assignment: Exclude<T["assignment"], undefined> | null;
  };
}

export type MotionAmplificationPreset =
  | "standard-60fps"
  | "high-frequency-240fps"
  | "high-frequency-120fps"
  | "high-stability-review";

export type MotionAmplificationParameters = {
  /** Detected from the source video; stored as metadata, not an override. */
  fps?: number;
  frequencyLowHz: number;
  frequencyHighHz: number;
  alpha: number;
  lambdaC: number;
  stabilize: boolean;
  stabilizeSmooth: number;
  maxWidth: number | null;
  pyramidLevels: number;
  chromAttenuation: number;
  chunkSize: number;
  overlap: number | null;
  stabilizeSmoothPasses: number;
  stabilizeMaxFeatures: number;
  stabilizeRansacPx: number;
  stabilizeFastThreshold: number;
  crop: { x: number; y: number; width: number; height: number } | null;
  heatmap: boolean;
  sideBySide: boolean;
  heatmapAlpha: number;
};

export class ModalityApiError extends Error {
  readonly statusCode: number;

  constructor(message: string, statusCode: number) {
    super(message);
    this.name = "ModalityApiError";
    this.statusCode = statusCode;
  }
}

function errorMessageFromResponseBody(body: unknown): string | undefined {
  if (typeof body !== "object" || body === null) return undefined;
  const { error, message } = body as Record<string, unknown>;
  if (typeof error === "string") return error;
  if (typeof message === "string") return message;
  return undefined;
}

async function parseModalityApiResponse<T>(
  response: Response,
  schema: z.ZodType<T>,
): Promise<T> {
  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null);
    throw new ModalityApiError(
      errorMessageFromResponseBody(body) ??
        `Modality API request failed (${response.status}).`,
      response.status,
    );
  }

  return schema.parse(await response.json());
}

export async function createMotionAmplificationUpload(
  input: {
    source?: "upload" | "existing";
    name: string;
    description?: string | null;
    fileName?: string;
    fileSize?: number;
    contentType?: string;
    width?: number;
    height?: number;
    rawObjectKey?: string;
    machineId?: string | null;
    preset: MotionAmplificationPreset;
    /** Detected from the selected video; stored as job metadata, not an override. */
    fps?: number;
    parameters: MotionAmplificationParameters;
  },
  idempotencyKey: string,
) {
  const response = await modalityClient.v1.uploads.$post({
    json: {
      ...input,
      ...(input.machineId ? { machineId: asMachineId(input.machineId) } : {}),
    },
    header: { "Idempotency-Key": idempotencyKey },
  });
  return withMotionAmplificationJobId(
    await parseModalityApiResponse(response, createUploadResponseSchema),
  );
}

export function uploadMotionAmplificationVideo(
  uploadUrl: string,
  file: File,
  onProgress?: (progress: number) => void,
  options: { signal?: AbortSignal; timeoutMs?: number } = {},
) {
  return new Promise<void>((resolve, reject) => {
    const request = new XMLHttpRequest();
    let lastProgress = -1;

    function cleanup() {
      options.signal?.removeEventListener("abort", abortRequest);
    }

    function resolveUpload() {
      cleanup();
      resolve();
    }

    function rejectUpload(error: Error) {
      cleanup();
      reject(error);
    }

    function abortRequest() {
      request.abort();
    }

    if (options.signal?.aborted) {
      rejectUpload(new Error("S3 upload was cancelled."));
      return;
    }

    request.open("PUT", uploadUrl);
    request.timeout = options.timeoutMs ?? 14 * 60 * 1000;
    request.setRequestHeader("Content-Type", file.type || "video/mp4");
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const progress = Math.floor((event.loaded / event.total) * 100);
        if (progress !== lastProgress) {
          lastProgress = progress;
          onProgress?.(progress);
        }
      }
    };
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        resolveUpload();
      } else if (request.status === 403) {
        rejectUpload(
          new Error(
            "S3 upload was rejected (403). The upload URL may have expired.",
          ),
        );
      } else {
        rejectUpload(new Error(`S3 upload failed (HTTP ${request.status}).`));
      }
    };
    request.onerror = () =>
      rejectUpload(new Error("S3 upload failed due to a network error."));
    request.onabort = () => rejectUpload(new Error("S3 upload was cancelled."));
    request.ontimeout = () => rejectUpload(new Error("S3 upload timed out."));
    options.signal?.addEventListener("abort", abortRequest, { once: true });
    request.send(file);
  });
}

export async function startMotionAmplificationJob(
  jobId: MotionAmplificationJobId,
) {
  const response = await modalityClient.v1.jobs[":jobId"].start.$post({
    param: { jobId },
  });
  return withMotionAmplificationJobId(
    await parseModalityApiResponse(response, startJobResponseSchema),
  );
}

export async function reportMotionAmplificationJobFailure(
  jobId: MotionAmplificationJobId,
  input: { phase: string; errorMessage: string },
) {
  const response = await modalityClient.v1.jobs[":jobId"].fail.$post({
    param: { jobId },
    json: input,
  });
  return withMotionAmplificationJobId(
    await parseModalityApiResponse(response, reportJobFailureResponseSchema),
  );
}

export async function getMotionAmplificationJob(
  jobId: MotionAmplificationJobId,
): Promise<MotionAmplificationJobDetail> {
  const response = await modalityClient.v1.jobs[":jobId"].$get({
    param: { jobId },
  });
  return withMotionAmplificationJobAssignment(
    await parseModalityApiResponse(
      response,
      motionAmplificationJobDetailSchema,
    ),
  );
}

export async function getMotionAmplificationJobDownloadUrls(
  jobId: MotionAmplificationJobId,
): Promise<MotionAmplificationJobDownloadUrls> {
  const response = await modalityClient.v1.jobs[":jobId"].downloads.$get({
    param: { jobId },
  });
  return parseModalityApiResponse(
    response,
    motionAmplificationJobDownloadUrlsSchema,
  );
}

export async function updateMotionAmplificationJob(
  jobId: MotionAmplificationJobId,
  input: { name: string; machineId: string | null },
): Promise<MotionAmplificationJobDetail> {
  const response = await modalityClient.v1.jobs[":jobId"].$patch({
    param: { jobId },
    json: {
      name: input.name,
      machineId: input.machineId === null ? null : asMachineId(input.machineId),
    },
  });
  return withMotionAmplificationJobAssignment(
    await parseModalityApiResponse(
      response,
      motionAmplificationJobDetailSchema,
    ),
  );
}

export async function retryMotionAmplificationJob(
  jobId: MotionAmplificationJobId,
) {
  const response = await modalityClient.v1.jobs[":jobId"].retry.$post({
    param: { jobId },
  });
  return withMotionAmplificationJobId(
    await parseModalityApiResponse(response, startJobResponseSchema),
  );
}

export async function getMotionAmplificationJobs(input: {
  limit: number;
  cursor?: string;
}): Promise<MotionAmplificationJobsResponse> {
  const response = await modalityClient.v1.jobs.$get({ query: input });
  const body = await parseModalityApiResponse(
    response,
    motionAmplificationJobListSchema,
  );
  return {
    ...body,
    jobs: body.jobs.map(withMotionAmplificationJobAssignment),
  };
}

export async function getMotionAmplificationRawVideos() {
  const response = await modalityClient.v1["raw-videos"].$get();
  return parseModalityApiResponse(
    response,
    motionAmplificationRawVideoListSchema,
  );
}

export async function getMotionAmplificationAssignmentCustomers() {
  const response = await modalityClient.v1["assignment-customers"].$get();
  return parseModalityApiResponse(response, assignmentOptionsSchema);
}

export async function getMotionAmplificationAssignmentFacilities(
  customerId: string,
) {
  const response = await modalityClient.v1["assignment-customers"][
    ":customerId"
  ].facilities.$get({ param: { customerId } });
  return parseModalityApiResponse(response, assignmentOptionsSchema);
}

export async function getMotionAmplificationAssignmentMachines(
  facilityId: string,
) {
  const response = await modalityClient.v1["assignment-facilities"][
    ":facilityId"
  ].machines.$get({ param: { facilityId } });
  return parseModalityApiResponse(response, assignmentOptionsSchema);
}
